import React, { useCallback, useEffect, useState } from "react";
import { StarRating } from "@/components/StarRating";
import { ChevronLeft, ChevronRight, Quote, User, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { SITE_CONTAINER } from "@/lib/siteLayout";

/**
 * Kiemelt vélemény carousel a Hero szekció alá.
 * - Auto-forgás 6 mp-enként, hover-ra megáll.
 * - Egyetlen vélemény a fókuszban, dot indicator + prev/next.
 * - Bal oldalt: aggregátum (átlag csillag + darabszám), CTA a #velemenyek horgonyra.
 */
export function FeaturedReviewsCarousel() {
  const { content } = useSiteContent();
  const fr = content.featured_reviews || DEFAULT_SITE_CONTENT.featured_reviews;
  const autoRotateMs = Number(fr.auto_rotate_ms) || 6000;
  const [reviews, setReviews] = useState([]);
  const [aggregate, setAggregate] = useState({ count: 0, average: 0 });
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/site-reviews");
      const all = r.data.reviews || [];
      // 4+ csillagos, hosszabb véleményeket priorizálunk (kiemelt)
      const featured = [...all]
        .filter((x) => x.rating >= 4 && (x.text || "").length >= 20)
        .sort((a, b) => b.rating - a.rating);
      setReviews(featured.length ? featured : all);
      setAggregate(r.data.aggregate || { count: 0, average: 0 });
    } catch (_) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (paused || reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), autoRotateMs);
    return () => clearInterval(t);
  }, [paused, reviews.length, autoRotateMs]);

  if (loading) {
    return (
      <section className="-mt-10 md:-mt-12 relative z-20">
        <div className={`${SITE_CONTAINER} glass-strong rounded-3xl p-6 md:p-8 flex justify-center`}>
          <Loader2 className="w-5 h-5 text-electric animate-spin" />
        </div>
      </section>
    );
  }

  if (!reviews.length) return null;

  const r = reviews[idx];
  const scrollToAll = () => {
    const el = document.getElementById("velemenyek");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="featured-reviews-carousel"
      className="-mt-10 md:-mt-14 relative z-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={`${SITE_CONTAINER} glass-strong rounded-3xl p-5 md:p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6 lg:gap-8 items-center`}>
        {/* Aggregate side */}
        <button
          type="button"
          data-testid="featured-reviews-aggregate"
          onClick={scrollToAll}
          className="text-left group"
          title="Összes vélemény megtekintése"
        >
          <div className="text-xs tracking-[0.15em] uppercase text-slate-400 mb-1">{fr.eyebrow}</div>
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-5xl font-light text-electric leading-none" data-testid="featured-reviews-avg">
              {aggregate.average.toFixed(1)}
            </div>
            <div className="text-sm text-slate-400">/ 5</div>
          </div>
          <div className="mt-2">
            <StarRating value={aggregate.average} size="md" />
          </div>
          <div className="text-xs text-slate-400 mt-2 group-hover:text-electric transition-colors">
            <span data-testid="featured-reviews-count">{aggregate.count}</span> {fr.aggregate_suffix}
          </div>
        </button>

        {/* Divider (desktop) */}
        <div className="hidden lg:block absolute left-[300px] top-8 bottom-8 w-px bg-white/10" aria-hidden />

        {/* Featured review */}
        <div className="relative min-h-[140px]">
          <Quote className="absolute -top-2 -left-1 w-8 h-8 text-electric/25" strokeWidth={1.5} />
          <div key={r.id} className="animate-in fade-in duration-500">
            <p
              className="text-base md:text-lg leading-relaxed text-slate-100 pl-8"
              data-testid="featured-review-body"
            >
              {r.text.length > 240 ? `${r.text.slice(0, 240)}…` : r.text}
            </p>
            <div className="mt-4 pl-8 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-electric/15 flex items-center justify-center text-electric shrink-0">
                <User className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate" data-testid="featured-review-author">{r.author_name}</div>
                <div className="mt-0.5"><StarRating value={r.rating} size="xs" /></div>
              </div>
              {reviews.length > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    data-testid="featured-reviews-prev"
                    onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
                    className="w-8 h-8 rounded-full glass hover:bg-electric hover:text-white flex items-center justify-center text-slate-300 transition-colors"
                    aria-label="Előző"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                  <button
                    data-testid="featured-reviews-next"
                    onClick={() => setIdx((i) => (i + 1) % reviews.length)}
                    className="w-8 h-8 rounded-full glass hover:bg-electric hover:text-white flex items-center justify-center text-slate-300 transition-colors"
                    aria-label="Következő"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {reviews.length > 1 && (
            <div className="mt-4 pl-8 flex items-center gap-1.5" data-testid="featured-reviews-dots">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  data-testid={`featured-reviews-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-electric" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
                  aria-label={`${i + 1}. vélemény`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
